/**
 * `Search` — the chalk line, the recent stamps, the grouped results, and the drawer
 * index behind them. `docs/design-direction.md` §8.3, `spec.md` §6.5.
 *
 * ## There is no search box
 *
 * The input is a chalk line: Display 800 `--fs-20` caps sitting on a 1px `--border`
 * underline that thickens to 2px `--accent` on focus, with a warm-ink caret. The
 * placeholder is **mono caps** `ЧТО ИГРАЕМ?` in `--text-3` and it is a real element
 * rather than `::placeholder`, because §8.3 asks for a different family in the
 * placeholder than in the field and a pseudo-element cannot carry the type class that
 * `fonts.css` §4.4 degrades. The handwriting family is not used anywhere on this
 * screen: a placeholder must be readable at a glance, and that family has exactly two
 * licensed homes, neither of them here (§4.1, §12.15).
 *
 * ## What the network does, and when
 *
 * One request per settled keystroke, 180 ms after the last one (`spec.md` §6.5), and
 * the in-flight request is aborted the moment a newer one is issued —
 * `store/library`'s `runSearch` owns both the `AbortController` and the echoed-`query`
 * check that stops an out-of-order answer from replacing a newer one. Nothing here
 * duplicates that; this screen owns only the debounce.
 *
 * **The debounced call is `GET /api/search`, not `GET /api/search/suggest`.** §8.3's
 * screen is entirely grouped results with counts, per-group ordering and the
 * `matchFields` the underline markup needs, and the task also requires echoing
 * `backend` so a dead search box is a one-request diagnosis. `SuggestResponse` carries
 * none of those — no groups, no counts, no `matchFields`, no `backend` — so consuming
 * it would leave this screen unable to render what it is specified to render, and
 * calling both endpoints would put two requests behind every keystroke instead of the
 * one the acceptance criterion counts. The 180 ms and the abort-on-supersede are
 * exactly as specified; only the endpoint behind them is the one that can answer.
 *
 * **A superseded query never blanks the screen.** While the next answer is in flight
 * the previous results stay, dimmed. A loading state that replaces results the user
 * can already read is a downgrade, and there is no spinner in this app at all (§8.8,
 * §12.7) — the loading mark is the one-drop ruler advancing.
 *
 * ## Matching, across two scripts
 *
 * A Russian speaker types `вибз картель` and expects the Latin-titled records. The
 * cross-script recall is the query layer's job and it is already done: the server
 * indexes both scripts in `search_key`, ORs each token with its transliteration and
 * falls back to a trigram tier. This screen consumes that and does not re-implement
 * it. What it does own is the **markup**: which characters of a displayed string to
 * mark, computed with the same `normalizeText`/`translit` the index was built with, so
 * a match the server found is a match the eye can see. When the transliterator's known
 * miss rate means we cannot locate the substring, nothing is marked — an honest miss,
 * never a guessed one.
 *
 * A marked run gets `--accent-tint` **and** a 1px `--accent` underline. Two carriers,
 * because at 13px a colour change alone is not a signal, and colour alone is never an
 * acceptable carrier of meaning.
 *
 * ## Recent queries
 *
 * Rubber stamps above the results, rotated alternately −2.5°/−4.5° so the row reads
 * hand-stamped. Tap refills the line; a 500 ms press strikes the mark through over
 * `--t16` and then removes it — a rule drawn across a word, which is how a list gets
 * crossed off. They live for the session in this module rather than in `store/persist`,
 * whose `UiPrefs` shape is owned by another task and is closed; a durable history needs
 * a field there, and that is noted rather than smuggled in.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, JSX } from 'react';

import { normalizeText, translit } from '@bl/shared';
import type {
  ArtistSummary,
  SearchArtistHit,
  SearchTagHit,
  SearchYearHit,
  TagSummary,
  TrackSummary,
} from '@bl/shared';

import type { ScreenProps } from '../app/router';
import { buildPath, navigate as defaultNavigate } from '../app/router';
import { Empty } from '../app/states/Empty';
import { ErrorState } from '../app/states/ErrorState';
import { Loading, StateFrame } from '../app/states/Loading';
import { getWiring } from '../app/wiring';
import type { TelegramFacade } from '../lib/telegram';
import { telegram as defaultTelegram } from '../lib/telegram';
import { Rule, cx } from '../components/primitives/Rule';
import { TrackRow } from '../components/TrackRow';
import { selectIsFavorite, useLibraryStore } from '../store/library';
import { useQueueStore } from '../store/queue';

import { DrawerIndex, IndexRow, STAGGER_CAP } from './DrawerIndex';
import styles from './search.module.css';

/* ══ copy and constants ════════════════════════════════════════════════════════════ */

/** §8.3, verbatim. Mono caps, `--text-3`, and never the handwriting family. */
export const PLACEHOLDER_RU = 'ЧТО ИГРАЕМ?';

/** The input's programmatic label. Sentence case (§4.3, §12.16). */
export const SEARCH_LABEL_RU = 'Поиск по ящику';

/** `spec.md` §6.5: "Debounced 180 ms client-side." */
export const DEBOUNCE_MS = 180;

/** How long a press has to be held before it becomes a delete (§8.5's long press). */
export const LONG_PRESS_MS = 500;

/**
 * How long the strike-through takes before the query is removed.
 *
 * §8.3 writes 160 ms; §7.1 closes the duration set and `--t16` (167 ms) is the value on
 * it, so the closed set wins — the two are indistinguishable at this length and a stray
 * duration is not worth the exception. The CSS animation and this timer are the same
 * number by construction.
 */
export const STRIKE_MS = 167;

/** How many recent queries the row keeps. Five stamps is already a busy row. */
export const RECENT_MAX = 5;

/**
 * Shortest token that may be marked in a result.
 *
 * A single character matches almost everything, and a screen where every other letter
 * is tinted carries no information at all. §6.5 draws the same line: the server adds
 * the prefix `*` only to a last token of two characters or more.
 */
export const MIN_MARK_LENGTH = 2;

/** The two stamp angles of §8.3, alternating down the row. */
const RECENT_TILTS = [-2.5, -4.5] as const;

/** §8.3's group headings, sentence case because the audience is Russian (§4.3, §12.16). */
export const GROUP_LABELS = Object.freeze({
  tracks: 'Треки',
  artists: 'Артисты',
  riddims: 'Риддимы',
  labels: 'Лейблы',
  tags: 'Теги',
  years: 'Годы',
});

/* ══ recent queries ════════════════════════════════════════════════════════════════ */

/**
 * Session-lived recent queries.
 *
 * Module scope rather than component state so they survive the screen being unmounted
 * and remounted by the router, and not exported directly because a mutable export is
 * banned outright — the two functions below are the whole surface.
 */
let recentQueries: readonly string[] = [];

/** The current recent-query list. Exported for tests and for a future persistence sink. */
export function getRecentQueries(): readonly string[] {
  return recentQueries;
}

/** Drops the session history. For tests, and for a future "clear history" control. */
export function resetRecentQueries(): void {
  recentQueries = [];
}

/** Most recent first, de-duplicated case-insensitively, capped at {@link RECENT_MAX}. */
export function pushRecentQuery(
  existing: readonly string[],
  query: string,
  max: number = RECENT_MAX,
): string[] {
  const trimmed = query.trim();
  if (trimmed === '') return [...existing];
  const key = normalizeText(trimmed);
  const kept = existing.filter((entry) => normalizeText(entry) !== key);
  return [trimmed, ...kept].slice(0, Math.max(1, max));
}

/* ══ highlighting ══════════════════════════════════════════════════════════════════ */

/** One run of a displayed string: marked, or not. */
export interface MarkedPart {
  readonly text: string;
  readonly marked: boolean;
}

/**
 * The tokens a displayed string is marked against: the normalised query tokens plus
 * each one's transliteration, de-duplicated.
 *
 * The transliteration is what makes `картель` mark `Kartel` — the same union the index
 * is built from (`buildSearchKey`), applied to the display side.
 */
export function markTokens(query: string): string[] {
  const normalized = normalizeText(query);
  if (normalized === '') return [];

  const out: string[] = [];
  const seen = new Set<string>();
  for (const token of normalized.split(' ')) {
    if (token.length < MIN_MARK_LENGTH) continue;
    for (const variant of [token, normalizeText(translit(token))]) {
      if (variant.length < MIN_MARK_LENGTH || seen.has(variant)) continue;
      seen.add(variant);
      out.push(variant);
    }
  }
  return out;
}

/**
 * A character that no normalised token can contain, standing in for every character
 * that normalises away (a space, a hyphen, a combining mark). Keeping a placeholder
 * rather than dropping the character is what stops `ab` from matching across the gap
 * in `a-b`, and it keeps the folded string index-aligned with the display string.
 */
const GAP = ' ';

interface Folded {
  /** The normalised projection of the display string. */
  readonly folded: string;
  /** `map[i]` is the display offset the folded character at `i` came from. */
  readonly map: readonly number[];
}

/**
 * Fold a display string the way the index folds it, keeping an offset map back.
 *
 * Per character, because `normalizeText` on a whole string collapses runs and trims,
 * which destroys the alignment the markup needs. A character may fold to more than one
 * (`ß` → `ss`), so the map is built rather than assumed.
 */
function foldWithMap(text: string): Folded {
  let folded = '';
  const map: number[] = [];
  let offset = 0;

  for (const ch of text) {
    const piece = normalizeText(ch);
    if (piece === '') {
      folded += GAP;
      map.push(offset);
    } else {
      for (const c of piece) {
        folded += c;
        map.push(offset);
      }
    }
    offset += ch.length;
  }
  map.push(text.length);

  return { folded, map };
}

/** Merge overlapping/abutting ranges so a doubly-matched run is marked once. */
function mergeRanges(ranges: readonly (readonly [number, number])[]): [number, number][] {
  const sorted = [...ranges].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const out: [number, number][] = [];
  for (const [from, to] of sorted) {
    const last = out[out.length - 1];
    if (last !== undefined && from <= last[1]) {
      if (to > last[1]) last[1] = to;
      continue;
    }
    out.push([from, to]);
  }
  return out;
}

/**
 * Split `text` into marked and unmarked runs.
 *
 * Returns a single unmarked part when nothing matches, so a caller can render the
 * result of this function unconditionally.
 */
export function markParts(text: string, tokens: readonly string[]): MarkedPart[] {
  if (text === '' || tokens.length === 0) return [{ text, marked: false }];

  const { folded, map } = foldWithMap(text);
  const ranges: [number, number][] = [];

  for (const token of tokens) {
    let at = folded.indexOf(token);
    while (at >= 0) {
      const from = map[at] ?? 0;
      const to = map[at + token.length] ?? text.length;
      if (to > from) ranges.push([from, to]);
      at = folded.indexOf(token, at + 1);
    }
  }

  if (ranges.length === 0) return [{ text, marked: false }];

  const parts: MarkedPart[] = [];
  let cursor = 0;
  for (const [from, to] of mergeRanges(ranges)) {
    if (from > cursor) parts.push({ text: text.slice(cursor, from), marked: false });
    parts.push({ text: text.slice(from, to), marked: true });
    cursor = to;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), marked: false });
  return parts;
}

export interface MarkedProps {
  readonly text: string;
  readonly tokens: readonly string[];
}

/**
 * A displayed string with its matched runs marked.
 *
 * Two carriers, never one: the `--accent-tint` ground and the 1px `--accent` underline
 * are separate classes on the same element, and the test asserts both are present.
 */
export function Marked({ text, tokens }: MarkedProps): JSX.Element {
  const parts = useMemo(() => markParts(text, tokens), [text, tokens]);

  return (
    <>
      {parts.map((part, index) =>
        part.marked ? (
          <mark key={index} className={cx(styles.match, styles.matchLine)} data-match="true">
            {part.text}
          </mark>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  );
}

/* ══ props ═════════════════════════════════════════════════════════════════════════ */

type LibraryStoreHook = typeof useLibraryStore;
type QueueStoreHook = typeof useQueueStore;

export interface SearchProps extends Partial<ScreenProps> {
  /** Injected by tests. Defaults to the app-wide slices. */
  readonly library?: LibraryStoreHook;
  readonly queue?: QueueStoreHook;
  readonly telegram?: TelegramFacade;
  readonly navigate?: (to: string) => void;
  /**
   * Plays a track hit. The default replaces the queue with the visible hits and starts
   * the tapped one — "play what I am looking at", which is what a selector means by
   * tapping a row in a result list.
   */
  readonly onPlay?: (hits: readonly TrackSummary[], index: number) => void;
}

/* ══ the screen ════════════════════════════════════════════════════════════════════ */

export function Search({
  library = useLibraryStore,
  queue = useQueueStore,
  telegram = defaultTelegram,
  navigate = defaultNavigate,
  onPlay,
}: SearchProps = {}): JSX.Element {
  const search = library((state) => state.search);
  const artists = library((state) => state.artists);
  const tags = library((state) => state.tags);
  /* Subscribed so a filed record repaints its bracket — a result list changes for no
     other reason when one is filed. Read through `selectIsFavorite` at the call site. */
  const favorites = library((state) => state.favorites);

  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState<readonly string[]>(getRecentQueries);
  const [striking, setStriking] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pressRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const strikeRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressedRef = useRef(false);

  const normalized = normalizeText(query);
  const isZeroState = normalized === '';

  /* — the drawer index's data, loaded once ──────────────────────────────────────── */

  useEffect(() => {
    const state = library.getState();
    if (state.artists.status === 'idle') void state.loadArtists();
    if (state.tags.status === 'idle') void state.loadTags();
  }, [library]);

  /* — the debounce ──────────────────────────────────────────────────────────────── */

  const commitRecent = useCallback((value: string): void => {
    recentQueries = pushRecentQuery(recentQueries, value);
    setRecent(recentQueries);
  }, []);

  const runNow = useCallback(
    (value: string): void => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
      debounceRef.current = null;
      if (normalizeText(value) === '') {
        library.getState().clearSearch();
        return;
      }
      commitRecent(value);
      void library.getState().runSearch(value);
    },
    [library, commitRecent],
  );

  /*
   * One timer, restarted on every keystroke. A query that normalises to nothing — the
   * user typing spaces — issues no request at all and falls back to the drawer index:
   * §6.5 says a blank query is not an error, and the cheapest way to honour that is not
   * to ask.
   */
  useEffect(() => {
    if (debounceRef.current !== null) clearTimeout(debounceRef.current);

    if (normalizeText(query) === '') {
      debounceRef.current = null;
      library.getState().clearSearch();
      return;
    }

    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      void library.getState().runSearch(query);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
      debounceRef.current = null;
    };
  }, [query, library]);

  /* Timers must not outlive the screen: a fired long-press on an unmounted tree is a
     state update into nothing. */
  useEffect(
    () => () => {
      if (pressRef.current !== null) clearTimeout(pressRef.current);
      if (strikeRef.current !== null) clearTimeout(strikeRef.current);
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    },
    [],
  );

  /* — handlers ──────────────────────────────────────────────────────────────────── */

  const haptic = useCallback(
    (style: 'light' | 'rigid'): void => {
      telegram.haptics.impact(style);
    },
    [telegram],
  );

  const fill = useCallback(
    (value: string): void => {
      setQuery(value);
      runNow(value);
    },
    [runNow],
  );

  const clearQuery = useCallback((): void => {
    setQuery('');
    library.getState().clearSearch();
  }, [library]);

  const startPress = useCallback(
    (value: string): void => {
      longPressedRef.current = false;
      if (pressRef.current !== null) clearTimeout(pressRef.current);
      pressRef.current = setTimeout(() => {
        pressRef.current = null;
        longPressedRef.current = true;
        haptic('rigid');
        setStriking(value);
        strikeRef.current = setTimeout(() => {
          strikeRef.current = null;
          recentQueries = recentQueries.filter((entry) => entry !== value);
          setRecent(recentQueries);
          setStriking(null);
        }, STRIKE_MS);
      }, LONG_PRESS_MS);
    },
    [haptic],
  );

  const endPress = useCallback((): void => {
    if (pressRef.current !== null) clearTimeout(pressRef.current);
    pressRef.current = null;
  }, []);

  const tapRecent = useCallback(
    (value: string): void => {
      if (longPressedRef.current) {
        longPressedRef.current = false;
        return;
      }
      haptic('light');
      fill(value);
    },
    [fill, haptic],
  );

  const openArtist = useCallback(
    (id: number): void => {
      haptic('light');
      navigate(buildPath('artist', { id: String(id) }));
    },
    [haptic, navigate],
  );

  const openTag = useCallback(
    (id: number): void => {
      haptic('light');
      navigate(buildPath('tag', { id: String(id) }));
    },
    [haptic, navigate],
  );

  /* — results ───────────────────────────────────────────────────────────────────── */

  const tokens = useMemo(() => markTokens(query), [query]);

  const groups = search.groups;
  const trackHits = groups.tracks?.items ?? [];
  const artistHits = groups.artists?.items ?? [];
  const yearHits = groups.years?.items ?? [];
  const tagHits = groups.tags?.items ?? [];

  const riddimHits = tagHits.filter((tag) => tag.kind === 'riddim');
  const labelHits = tagHits.filter((tag) => tag.kind === 'label');
  const otherTagHits = tagHits.filter((tag) => tag.kind !== 'riddim' && tag.kind !== 'label');

  /*
   * A split subgroup can only print a count when the parent group is complete: then the
   * rows on screen ARE all of them. When the server truncated the group, the true
   * per-kind total is unknown and nothing numeric is printed (§8.8, §12.6).
   */
  const tagsComplete = groups.tags !== undefined && groups.tags.total === tagHits.length;
  const subCount = (items: readonly unknown[]): number | null =>
    tagsComplete ? items.length : null;

  const total = trackHits.length + artistHits.length + tagHits.length + yearHits.length;
  const hasResults = total > 0;

  const playHits = useCallback(
    (index: number): void => {
      haptic('light');
      const hits = library.getState().search.groups.tracks?.items ?? [];
      if (onPlay !== undefined) {
        onPlay(hits, index);
        return;
      }
      void queue
        .getState()
        .replace(hits, index)
        .then(() => getWiring().select(index));
    },
    [haptic, library, onPlay, queue],
  );

  const suggestions = useMemo(
    () =>
      [...artists.items]
        .sort((a, b) => b.trackCount - a.trackCount)
        .slice(0, 3)
        .map((artist) => artist.name),
    [artists.items],
  );

  /* — the chalk line ────────────────────────────────────────────────────────────── */

  const bar = (
    <form
      className={styles.bar}
      role="search"
      data-bar="true"
      onSubmit={(event) => {
        event.preventDefault();
        runNow(query);
      }}
    >
      <label className={styles.line} data-line="true">
        <span className={styles.sr}>{SEARCH_LABEL_RU}</span>
        <input
          className={cx('t-display', 't-20', styles.input)}
          type="search"
          name="q"
          value={query}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          data-input="search"
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        {query === '' ? (
          <span
            className={cx('t-mono', 't-13', styles.cyr, styles.placeholder)}
            data-placeholder="true"
            aria-hidden="true"
          >
            {PLACEHOLDER_RU}
          </span>
        ) : null}
      </label>

      <p className={cx('t-mono', 't-11', styles.readout)} data-readout="true">
        <span data-count="true">{hasResults ? String(total) : ''}</span>
        {/* The echoed backend of §6.5: which query path actually answered. Printed as
            received, never round-tripped through anything, and absent until a real
            answer has arrived — it is a diagnosis, not a decoration. */}
        {search.backend === null ? null : (
          <span data-backend={search.backend}>{search.backend.toUpperCase()}</span>
        )}
      </p>
    </form>
  );

  const recentRow =
    recent.length === 0 ? null : (
      <div className={styles.recent} data-recent="true">
        {recent.map((value, index) => (
          <button
            key={value}
            type="button"
            className={cx(
              't-mono',
              't-11',
              styles.cyr,
              styles.stampish,
              striking === value && styles.striking,
            )}
            style={
              {
                '--tilt': `${RECENT_TILTS[index % RECENT_TILTS.length] ?? -2.5}deg`,
              } as CSSProperties
            }
            data-recent-query={value}
            data-striking={striking === value ? 'true' : undefined}
            onPointerDown={() => {
              startPress(value);
            }}
            onPointerUp={endPress}
            onPointerLeave={endPress}
            onPointerCancel={endPress}
            onClick={() => {
              tapRecent(value);
            }}
          >
            {value}
            {striking === value ? (
              <span className={styles.strike} data-strike="true" aria-hidden="true" />
            ) : null}
          </button>
        ))}
      </div>
    );

  /* — the body ──────────────────────────────────────────────────────────────────── */

  let body: JSX.Element;

  if (isZeroState) {
    body = (
      <DrawerIndex
        artists={artists.items}
        tags={tags.items}
        status={artists.status === 'ready' ? tags.status : artists.status}
        error={artists.error ?? tags.error}
        onRetry={() => {
          void library.getState().loadArtists();
          void library.getState().loadTags();
        }}
        onSelectArtist={(artist: ArtistSummary) => {
          openArtist(artist.id);
        }}
        onSelectTag={(tag: TagSummary) => {
          openTag(tag.id);
        }}
        onPickChip={fill}
      />
    );
  } else if (search.status === 'error' && search.error !== null) {
    body = (
      <ErrorState
        failure={search.error}
        grid="ledger"
        onRetry={() => {
          runNow(query);
        }}
      />
    );
  } else if (!hasResults && search.status === 'loading') {
    body = <Loading grid="ledger" rows={6} />;
  } else if (!hasResults && search.status === 'ready') {
    body = (
      <>
        <Empty variant="search" grid="ledger" onAction={clearQuery} />
        {suggestions.length === 0 ? null : (
          <div className={styles.recent} data-suggestions="true">
            {suggestions.map((value, index) => (
              <button
                key={value}
                type="button"
                className={cx(
                  't-mono',
                  't-11',
                  styles.cyr,
                  styles.stampish,
                  index === STAGGER_CAP && styles.stagger,
                )}
                style={
                  {
                    '--tilt': `${RECENT_TILTS[index % RECENT_TILTS.length] ?? -2.5}deg`,
                  } as CSSProperties
                }
                data-suggestion={value}
                onClick={() => {
                  fill(value);
                }}
              >
                {value}
              </button>
            ))}
          </div>
        )}
      </>
    );
  } else {
    body = (
      <StateFrame
        grid="ledger"
        state="content"
        className={cx(search.status === 'loading' && styles.stale)}
        id="bl-search-results"
      >
        {trackHits.length === 0 ? null : (
          <section className={styles.group} data-group="tracks">
            <ResultHeading label={GROUP_LABELS.tracks} count={groups.tracks?.total ?? null} />
            <ul>
              {trackHits.map((track, index) => (
                <li
                  key={track.id}
                  className={styles.stagger}
                  style={{ '--i': String(Math.min(index, STAGGER_CAP - 1)) } as CSSProperties}
                >
                  <TrackRow
                    title={track.title}
                    performer={track.performer}
                    durationS={track.durationS}
                    messageId={track.id}
                    seed={track.plate.seed}
                    meta={[track.year, track.bpm === null ? null : `BPM ${track.bpm}`]}
                    index={index}
                    onPlay={() => {
                      playHits(index);
                    }}
                    filed={selectIsFavorite(track.id)({ favorites })}
                    onFile={() => {
                      void library.getState().toggleFavorite(track.id);
                    }}
                  />
                </li>
              ))}
            </ul>
          </section>
        )}

        {artistHits.length === 0 ? null : (
          <ResultGroup
            id="artists"
            label={GROUP_LABELS.artists}
            count={groups.artists?.total ?? null}
          >
            {artistHits.map((hit: SearchArtistHit, index) => (
              <IndexRow
                key={hit.id}
                label={<Marked text={hit.name} tokens={tokens} />}
                name={hit.name}
                count={hit.trackCount}
                index={index}
                onSelect={() => {
                  openArtist(hit.id);
                }}
              />
            ))}
          </ResultGroup>
        )}

        {riddimHits.length === 0 ? null : (
          <ResultGroup id="riddims" label={GROUP_LABELS.riddims} count={subCount(riddimHits)}>
            {riddimHits.map((hit: SearchTagHit, index) => (
              <TagResultRow key={hit.id} hit={hit} tokens={tokens} index={index} onOpen={openTag} />
            ))}
          </ResultGroup>
        )}

        {labelHits.length === 0 ? null : (
          <ResultGroup id="labels" label={GROUP_LABELS.labels} count={subCount(labelHits)}>
            {labelHits.map((hit: SearchTagHit, index) => (
              <TagResultRow key={hit.id} hit={hit} tokens={tokens} index={index} onOpen={openTag} />
            ))}
          </ResultGroup>
        )}

        {otherTagHits.length === 0 ? null : (
          <ResultGroup id="tags" label={GROUP_LABELS.tags} count={subCount(otherTagHits)}>
            {otherTagHits.map((hit: SearchTagHit, index) => (
              <TagResultRow key={hit.id} hit={hit} tokens={tokens} index={index} onOpen={openTag} />
            ))}
          </ResultGroup>
        )}

        {yearHits.length === 0 ? null : (
          <ResultGroup id="years" label={GROUP_LABELS.years} count={groups.years?.total ?? null}>
            {yearHits.map((hit: SearchYearHit, index) => (
              <IndexRow
                key={hit.year}
                label={<Marked text={String(hit.year)} tokens={tokens} />}
                name={String(hit.year)}
                count={hit.trackCount}
                index={index}
                onSelect={() => {
                  fill(String(hit.year));
                }}
              />
            ))}
          </ResultGroup>
        )}
      </StateFrame>
    );
  }

  return (
    <div className={styles.screen} data-screen="search" data-normalized={normalized}>
      {bar}
      {recentRow}
      {body}
    </div>
  );
}

/* ══ small pieces ══════════════════════════════════════════════════════════════════ */

interface ResultHeadingProps {
  readonly label: string;
  readonly count: number | null;
}

/**
 * A group heading: mono caps left, the count right, one printed rule beneath.
 *
 * The count is the API's own or nothing at all — a group never prints a number it
 * inferred (§8.8, §12.6). Cyrillic keeps the mono tier and drops the tracking, because
 * tracked uppercase Cyrillic reads as shouting (§4.3, §12.16).
 */
function ResultHeading({ label, count }: ResultHeadingProps): JSX.Element {
  return (
    <>
      <div className={styles.groupHead}>
        <h2 className={cx('t-mono', 't-13', styles.cyr)}>{label}</h2>
        {count === null ? null : (
          <span
            className={cx('t-mono', 't-11', styles.groupCount)}
            data-group-count={String(count)}
          >
            {count}
          </span>
        )}
      </div>
      <Rule />
    </>
  );
}

interface ResultGroupProps {
  readonly id: string;
  readonly label: string;
  readonly count: number | null;
  readonly children: JSX.Element[];
}

function ResultGroup({ id, label, count, children }: ResultGroupProps): JSX.Element {
  return (
    <section className={styles.group} data-group={id}>
      <ResultHeading label={label} count={count} />
      {children}
    </section>
  );
}

interface TagResultRowProps {
  readonly hit: SearchTagHit;
  readonly tokens: readonly string[];
  readonly index: number;
  readonly onOpen: (id: number) => void;
}

function TagResultRow({ hit, tokens, index, onOpen }: TagResultRowProps): JSX.Element {
  return (
    <IndexRow
      label={<Marked text={hit.name} tokens={tokens} />}
      name={hit.name}
      count={hit.trackCount}
      index={index}
      onSelect={() => {
        onOpen(hit.id);
      }}
    />
  );
}
